import React, { useState, useEffect, useRef } from 'react';
import { EdgeProps, useReactFlow } from '@xyflow/react';

export const CustomSmoothEdge = (props: EdgeProps) => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    data,
    selected,
    style = {},
  } = props;

  const stroke = style.stroke || '#000';
  const strokeWidth = Number(style.strokeWidth) || 1;
  const interactionWidth = 20;

  const arrowColor = stroke;
  const arrowWidth = 6;
  const arrowLength = 10;
  const highlightColor = stroke;
  const glowColor = stroke;

  const [control, setControl] = useState<{ x: number; y: number }>({
    x: Number(data?.controlX) || (sourceX + targetX) / 2,
    y: Number(data?.controlY) || (sourceY + targetY) / 2,
  });

  const prevEndpoints = useRef({ sourceX, sourceY, targetX, targetY });

  useEffect(() => {
  if (data?.controlX != null && data?.controlY != null) return;

  const newMid = {
    x: (sourceX + targetX) / 2,
    y: (sourceY + targetY) / 2,
  };
  setControl(newMid);
  prevEndpoints.current = { sourceX, sourceY, targetX, targetY };
  }, [sourceX, sourceY, targetX, targetY]);

  const { updateEdgeData } = useReactFlow();
  useEffect(() => {
    updateEdgeData(id, { controlX: control.x, controlY: control.y });
  }, [control]);

  const getPointOnQuadraticBezier = (t: number, p0: number, p1: number, p2: number) =>
    (1 - t) * (1 - t) * p0 + 2 * (1 - t) * t * p1 + t * t * p2;

  const getMidPoint = () => ({
    x: getPointOnQuadraticBezier(0.5, sourceX, control.x, targetX),
    y: getPointOnQuadraticBezier(0.5, sourceY, control.y, targetY),
  });

  const calculateControlFromMidpoint = (px: number, py: number) => ({
    x: 2 * px - 0.5 * (sourceX + targetX),
    y: 2 * py - 0.5 * (sourceY + targetY),
  });

  const handleDrag = (event: PointerEvent) => {
    const svg = (event.target as SVGElement).ownerSVGElement;
    if (svg && svg.getScreenCTM) {
      const point = svg.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      const transformed = point.matrixTransform(svg.getScreenCTM()?.inverse());
      const newControl = calculateControlFromMidpoint(transformed.x, transformed.y);
      setControl(newControl);
    }
  };

  const edgePath = `M ${sourceX},${sourceY} Q ${control.x},${control.y} ${targetX},${targetY}`;
  const midPoint = getMidPoint();

  return (
    <>
      <defs>
        <filter id={`glow-${id}`}>
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feFlood floodColor={glowColor} floodOpacity="0.4" />
          <feComposite in2="coloredBlur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <marker
          id={`arrow-${id}`}
          viewBox="0 0 10 10"
          refX={10}
          refY={5}
          markerWidth={arrowWidth}
          markerHeight={arrowLength}
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={arrowColor} />
        </marker>
      </defs>

      {interactionWidth && (
        <path
          d={edgePath}
          fill="none"
          strokeOpacity={0}
          strokeWidth={interactionWidth}
          className="react-flow__edge-interaction"
        />
      )}

      <path id={id} d={edgePath} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={style.strokeDasharray}/>
      <path
        id={`${id}B`}
        d={edgePath}
        fill="none"
        stroke={stroke}
        strokeWidth={Math.min(strokeWidth, 2)}
        markerEnd={`url(#arrow-${id})`}
        strokeDasharray={style.strokeDasharray}
        filter={selected ? `url(#glow-${id})` : undefined}
        />
      <circle
        cx={midPoint.x}
        cy={midPoint.y}
        r={6}
        fill="#fff"
        stroke={highlightColor}
        strokeWidth={2}
        cursor="move"
        pointerEvents="all"
        onPointerDown={(event) => {
          event.preventDefault();
          const target = event.target as SVGCircleElement;
          target.setPointerCapture(event.pointerId);
          target.onpointermove = handleDrag as any;
          target.onpointerup = () => (target.onpointermove = null);
        }}
      />
    </>
  );
};
